/* Pause/resume hook-up for canvas & gsap animations: stops the loop when
 * the element scrolls out of view (with a small margin), while the page
 * is actively scrolling, or when the tab is hidden — restarts it when
 * conditions clear. Returns an unbind function.
 *
 * The scroll gate is the scroll-performance lever: with several canvases
 * near the viewport, all of them drawing every frame WHILE the page
 * scrolls is what makes scrolling feel laggy. Decorative motion freezes
 * during the scroll and resumes ~160ms after it settles.
 */

interface Entry {
  update: () => void;
  setScrolling: (s: boolean) => void;
}

const entries = new Set<Entry>();
let scrolling = false;
let scrollT = 0;
let scrollHooked = false;

function hookScroll() {
  if (scrollHooked || typeof window === "undefined") return;
  scrollHooked = true;
  /* phones scroll natively past the figures — freezing them mid-scroll
     reads as the animation stuttering, and one canvas at a time is cheap
     enough to keep drawing; the gate stays desktop-only */
  const phone = window.matchMedia("(max-width: 768px)");
  window.addEventListener(
    "scroll",
    () => {
      if (phone.matches) return;
      if (!scrolling) {
        scrolling = true;
        entries.forEach((e) => e.setScrolling(true));
      }
      window.clearTimeout(scrollT);
      scrollT = window.setTimeout(() => {
        scrolling = false;
        entries.forEach((e) => e.setScrolling(false));
      }, 160);
    },
    { passive: true }
  );
}

export function bindAnimPause(
  el: Element,
  start: () => void,
  stop: () => void
): () => void {
  hookScroll();
  let inView = true;
  let visible = !document.hidden;
  let still = !scrolling;
  let running = true;
  const update = () => {
    const should = inView && visible && still;
    if (should === running) return;
    running = should;
    if (should) start();
    else stop();
  };
  const entry: Entry = {
    update,
    setScrolling: (s: boolean) => {
      still = !s;
      update();
    },
  };
  entries.add(entry);
  const io = new IntersectionObserver(
    (ioEntries) => {
      inView = ioEntries[0].isIntersecting;
      update();
    },
    { rootMargin: "120px" }
  );
  io.observe(el);
  const onVis = () => {
    visible = !document.hidden;
    update();
  };
  document.addEventListener("visibilitychange", onVis);
  return () => {
    entries.delete(entry);
    io.disconnect();
    document.removeEventListener("visibilitychange", onVis);
  };
}
